import {
  BookingTourBasicDataDto,
  PublicTourPriceWebDto,
} from '@/features/public/booking/types/BookingTourBasicDataDto.ts';
import { formatPriceRowLabel } from '@/features/public/booking/components/tourInfoFormatters.ts';
import Chip from '@/components/ui/Chip.tsx';
import { CalendarDays, MapPin } from 'lucide-react';
import timeUtils from '@/utils/timeUtils.ts';
import { formatPrice } from '@/utils/formatters.ts';
import { formatDestinationFull } from '@/features/dashboard/tours/types/DestinationDto.utils.ts';
import { cn } from '@/lib/utils.ts';

/**
 * Treść bloku o wyjeździe rozbita na kawałki, bo rysują ją dwa układy: kolumna
 * obok formularza na szerokim ekranie i zwijane pudełko nad formularzem na
 * wąskim. Rozjazd między nimi byłby rozjazdem w danych, nie w stylach.
 */

/** Etykieta sekcji w bloku — jeden rozmiar w kolumnie i w pudełku. */
export const InfoSectionLabel = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('text-xs text-neutral-600', className)} {...props} />
);

export const TourOrganizer = ({
  tourBasicInfo,
  showLogo = true,
  className,
  ...props
}: {
  tourBasicInfo: BookingTourBasicDataDto;
  showLogo?: boolean;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-1', className)} {...props}>
    {showLogo && tourBasicInfo.organizerLogoUrl && (
      <img
        src={tourBasicInfo.organizerLogoUrl}
        alt={`${tourBasicInfo.organizerName} logo`}
        className="mb-2 max-h-36 object-contain object-left"
      />
    )}
    <InfoSectionLabel>Organizator</InfoSectionLabel>
    <p className="text-base font-semibold text-base-text">
      {tourBasicInfo.organizerName}
    </p>
  </div>
);

export const TourPriceList = ({
  prices,
  className,
  ...props
}: {
  prices: PublicTourPriceWebDto[];
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-2', className)} {...props}>
    <InfoSectionLabel>Podstawowy koszt na uczestnika</InfoSectionLabel>
    <div className="flex flex-col gap-1.5">
      {prices.map((entry, index) => {
        const label = formatPriceRowLabel(entry, prices.length);

        return (
          <div key={index} className="flex items-center justify-between gap-2">
            {label && <Chip label={label} tone="neutral" fill="soft" />}

            {/* Sama kwota zaczyna wiersz od lewej; dosunięta do prawej wisiałaby
                w pustce, bo nie ma z czym stanąć w parze. */}
            <span
              className={cn(
                'text-sm font-medium text-base-text',
                label && 'ml-auto',
              )}
            >
              {formatPrice(entry.price.amount, entry.price.currency)}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

export const TourDetailsList = ({
  tourBasicInfo,
  className,
  ...props
}: {
  tourBasicInfo: BookingTourBasicDataDto;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-2', className)} {...props}>
    <InfoSectionLabel>Informacje o wyjeździe</InfoSectionLabel>
    <div className="flex flex-col gap-1.5">
      {tourBasicInfo.destination && (
        <div className="flex items-start gap-2 text-sm text-base-text">
          <MapPin className="mt-0.5 size-4 shrink-0 text-neutral-500" />
          <span>{formatDestinationFull(tourBasicInfo.destination)}</span>
        </div>
      )}
      <div className="flex items-start gap-2 text-sm text-base-text">
        <CalendarDays className="mt-0.5 size-4 shrink-0 text-neutral-500" />
        <span>
          {timeUtils.toFullDateRangeWithDuration(
            tourBasicInfo.startDate,
            tourBasicInfo.endDate,
          )}
        </span>
      </div>
    </div>
  </div>
);
